import {
  Content,
  Body,
  Tabs,
  Tab,
  Group,
  ColorRow,
  ButtonAnchor,
  Header,
  EnableLabel,
  Summary,
  Notice,
  Title,
  PreviewArea,
  ColorControls,
  ColorValue,
  colorHexStyle,
  isEffectMenuTarget,
  Actions,
} from './textEffectStyles';
import { isEqual } from '../../../../../../../utils/isEqual';
import PropTypes from 'prop-types';
import {
  useCallback,
  useEffect,
  useFocusOut,
  useRef,
  useResizeEffect,
  useState,
} from '@web-stories-wp/react';
import { __, sprintf } from '@web-stories-wp/i18n';
import {
  Button,
  Checkbox,
  HexInput,
  Icons,
  BUTTON_VARIANTS,
  BUTTON_SIZES,
  BUTTON_TYPES,
  PLACEMENT,
} from '@web-stories-wp/design-system';
import { MULTIPLE_DISPLAY_VALUE, MULTIPLE_VALUE } from '../../../../constants';
import { Row } from '../../../form';
import {
  createSolidFromString,
  getHexFromSolid,
} from '@web-stories-wp/patterns';
import Popup from '../../../popup';
import useInspector from '../../../inspector/useInspector';
import useFocusTrapping from '../../../../utils/useFocusTrapping';
import { getCommonValue } from '../../shared';
import {
  DEFAULTS,
  FIELDS,
  SOURCE_OPTIONS,
  CONTOUR_OPTIONS,
  settingsFor,
} from './textInnerGlowSettings';
import TextInnerGlowPreview from './textInnerGlowPreview';
import { getTextEffectAlignmentUpdate } from '../../../../elements/text/utils/getTextEffectAlignmentUpdate';
import TextEffectBlendMode from './textEffectBlendMode';
import TextEffectNumericField from './textEffectNumericField';
import TextEffectSelect from './textEffectSelect';
import useFrameUpdates from './useFrameUpdates';
import EffectColorInput from './effectColorInput';

const TABS = [
  { id: 'essentials', label: __('Essentials', 'web-stories') },
  { id: 'advanced', label: __('Advanced', 'web-stories') },
];
const DIALOG_ID = 'text-inner-glow-settings-dialog';
const POPUP_SPACING = { x: 20 };

function InnerGlowDialog({ anchor, elements, onClose, onUpdate, onResize }) {
  const containerRef = useRef();
  useResizeEffect(containerRef, onResize, [onResize]);
  const [section, setSection] = useState('essentials');
  const values = elements.map((element) => ({
    ...element,
    textInnerGlow: settingsFor(element),
  }));
  const valueFor = (key) =>
    getCommonValue(values, ({ textInnerGlow }) => textInnerGlow[key]);
  const update = (getSettings) => {
    if (
      !elements.some(
        (element) => !isEqual(settingsFor(element), getSettings(element))
      )
    )
      return;
    const ids = new Set(elements.map(({ id }) => id));
    onUpdate((element) => {
      if (!ids.has(element.id)) return {};
      const next = getSettings(element);
      return isEqual(settingsFor(element), next)
        ? {}
        : {
            textInnerGlow: next,
            ...getTextEffectAlignmentUpdate({
              ...element,
              textInnerGlow: next,
            }),
          };
    });
  };
  const { queue, flush, cancel } = useFrameUpdates((changes) => {
    const patch = Object.fromEntries(
      Object.entries(changes).filter(([key, value]) => valueFor(key) !== value)
    );
    // Unchanged blur must not create history or upgrade a legacy recipe.
    if (!Object.keys(patch).length) return;
    update((element) => ({ ...settingsFor(element), ...patch }));
  });
  const reset = () => {
    cancel();
    update((element) => ({
      ...DEFAULTS,
      enabled: settingsFor(element).enabled,
    }));
  };
  const previewChange = (key, value) => queue({ [key]: value });
  const change = (key, value) => {
    previewChange(key, value);
    flush();
  };
  const closeAndRefocus = useCallback(() => {
    flush();
    anchor.current?.querySelector('button')?.focus();
    onClose();
  }, [anchor, flush, onClose]);
  useFocusOut(
    containerRef,
    (event) => {
      const target =
        event.type === 'focusout' ? event.relatedTarget : event.target;
      if (
        !containerRef.current?.contains(target) &&
        !anchor.current?.contains(target) &&
        !isEffectMenuTarget(containerRef.current, target)
      ) {
        flush();
        onClose();
      }
    },
    [anchor, flush, onClose]
  );
  useFocusTrapping({ ref: containerRef });
  useEffect(() => {
    const firstControl =
      containerRef.current?.querySelector('[aria-pressed="true"]') ||
      containerRef.current?.querySelector('button');
    firstControl?.focus();
  }, []);
  const field = (key, options = {}) => (
    <TextEffectNumericField
      key={key}
      field={FIELDS.find((field) => field.key === key)}
      value={valueFor(key)}
      onChange={(value) => change(key, value)}
      {...options}
    />
  );
  const colorField = () => {
    const value = valueFor('color');
    const isColor = typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value);
    return (
      <ColorRow>
        <span>{__('Color', 'web-stories')}</span>
        <ColorControls>
          <ColorValue>
            <EffectColorInput
              aria-label={__('Choose inner glow color', 'web-stories')}
              value={isColor ? value : '#808080'}
              onPreviewChange={(value) => previewChange('color', value)}
              onChange={(value) => change('color', value)}
              onBlur={flush}
            />
            <HexInput
              data-text-effect-hex=""
              aria-label={__('Inner glow color', 'web-stories')}
              onChangeCapture={(event) => {
                const hex = event.target.value.trim().replace(/^#/, '');
                if (/^[0-9a-f]{6}$/i.test(hex))
                  change('color', `#${hex.toLowerCase()}`);
              }}
              value={isColor ? createSolidFromString(value) : null}
              isIndeterminate={value === MULTIPLE_VALUE}
              placeholder={
                value === MULTIPLE_VALUE ? MULTIPLE_DISPLAY_VALUE : ''
              }
              containerStyleOverride={colorHexStyle}
              onChange={(solid) =>
                change('color', `#${getHexFromSolid(solid, true)}`)
              }
            />
          </ColorValue>
          {field('opacity', { compact: true })}
        </ColorControls>
      </ColorRow>
    );
  };
  return (
    <Content
      id={DIALOG_ID}
      role="dialog"
      aria-label={__('Inner glow settings', 'web-stories')}
      ref={containerRef}
      onKeyDownCapture={(event) => {
        // Let HexInput's native handler cancel before dismissal/refocusing
        // blurs the field. Already applied effect settings stay committed.
        if (
          event.key === 'Escape' &&
          !isEffectMenuTarget(containerRef.current, event.target)
        ) {
          if (event.target.hasAttribute('data-text-effect-hex')) {
            Promise.resolve().then(closeAndRefocus);
            return;
          }
          event.preventDefault();
          event.stopPropagation();
          closeAndRefocus();
        }
      }}
      onKeyDown={(event) => {
        // Portal keyboard events must not submit the enclosing inspector form.
        if (event.key === 'Enter' && event.target.tagName === 'INPUT')
          event.preventDefault();
      }}
    >
      <Header>
        <Title>{__('Inner glow settings', 'web-stories')}</Title>
        <Button
          type={BUTTON_TYPES.TERTIARY}
          size={BUTTON_SIZES.SMALL}
          variant={BUTTON_VARIANTS.SQUARE}
          aria-label={__('Close inner glow settings', 'web-stories')}
          onClick={closeAndRefocus}
        >
          <Icons.Cross />
        </Button>
      </Header>
      <PreviewArea>
        {__('Effect preview', 'web-stories')}
        <TextInnerGlowPreview textInnerGlow={settingsFor(elements[0])} />
      </PreviewArea>
      <Tabs
        role="tablist"
        aria-label={__('Inner glow settings sections', 'web-stories')}
        onKeyDown={(event) => {
          const index = TABS.findIndex(({ id }) => id === section);
          const next =
            event.key === 'ArrowRight'
              ? (index + 1) % TABS.length
              : event.key === 'ArrowLeft'
              ? (index + TABS.length - 1) % TABS.length
              : event.key === 'Home'
              ? 0
              : event.key === 'End'
              ? TABS.length - 1
              : null;
          if (next === null) return;
          event.preventDefault();
          setSection(TABS[next].id);
          event.currentTarget.querySelectorAll('[role="tab"]')[next]?.focus();
        }}
      >
        {TABS.map(({ id, label }) => (
          <Tab
            key={id}
            type="button"
            role="tab"
            id={`${DIALOG_ID}-tab-${id}`}
            aria-controls={`${DIALOG_ID}-panel-${id}`}
            aria-selected={section === id}
            tabIndex={section === id ? 0 : -1}
            onClick={() => setSection(id)}
          >
            {label}
          </Tab>
        ))}
      </Tabs>
      <Body
        key={section}
        role="tabpanel"
        id={`${DIALOG_ID}-panel-${section}`}
        aria-labelledby={`${DIALOG_ID}-tab-${section}`}
      >
        {elements.length > 1 && (
          <Notice>
            {__(
              'Preview uses the first layer’s settings. Changes apply to all selected text layers.',
              'web-stories'
            )}
          </Notice>
        )}
        {section === 'essentials' && (
          <Group>
            {colorField()}
            {field('size')}
          </Group>
        )}
        {section === 'advanced' && (
          <>
            <Group>
              <TextEffectBlendMode
                label={__('Inner glow blend mode', 'web-stories')}
                value={valueFor('blendMode')}
                onChange={(value) => change('blendMode', value)}
              />
              <TextEffectSelect
                label={__('Inner glow source', 'web-stories')}
                visibleLabel={__('Source', 'web-stories')}
                options={SOURCE_OPTIONS}
                value={valueFor('source')}
                onChange={(value) => change('source', value)}
              />
              {field('choke')}
              <TextEffectSelect
                label={__('Inner glow contour', 'web-stories')}
                visibleLabel={__('Contour', 'web-stories')}
                options={CONTOUR_OPTIONS}
                value={valueFor('contour')}
                onChange={(value) => change('contour', value)}
              />
              {field('range')}
            </Group>
          </>
        )}
      </Body>
      <Actions>
        <Button
          type={BUTTON_TYPES.TERTIARY}
          size={BUTTON_SIZES.SMALL}
          onClick={reset}
        >
          {__('Reset inner glow', 'web-stories')}
        </Button>
        <Button
          type={BUTTON_TYPES.SECONDARY}
          size={BUTTON_SIZES.SMALL}
          onClick={closeAndRefocus}
        >
          {__('Done', 'web-stories')}
        </Button>
      </Actions>
    </Content>
  );
}

InnerGlowDialog.propTypes = {
  anchor: PropTypes.shape({ current: PropTypes.instanceOf(Element) })
    .isRequired,
  elements: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onResize: PropTypes.func.isRequired,
};

function TextInnerGlowControls({ selectedElements, pushUpdate }) {
  const [session, setSession] = useState(null);
  const anchor = useRef();
  const {
    refs: { inspector },
  } = useInspector();
  const selectionKey = selectedElements.map(({ id }) => id).join(',');
  const enabled = getCommonValue(
    selectedElements,
    (element) => settingsFor(element).enabled
  );
  const summaryValue = (key) => {
    const value = getCommonValue(
      selectedElements,
      (element) => settingsFor(element)[key]
    );
    return value === MULTIPLE_VALUE ? MULTIPLE_DISPLAY_VALUE : value;
  };
  const color = summaryValue('color');
  const label =
    enabled === MULTIPLE_VALUE
      ? MULTIPLE_DISPLAY_VALUE
      : !enabled
      ? __('Off', 'web-stories')
      : sprintf(
          /* translators: 1: glow color, 2: opacity percentage, 3: size in pixels. */
          __('%1$s · %2$s%% · %3$s px', 'web-stories'),
          color === '#ffffff'
            ? __('White', 'web-stories')
            : String(color).toUpperCase(),
          summaryValue('opacity'),
          summaryValue('size')
        );
  const isOpen = session?.key === selectionKey;
  useEffect(() => {
    setSession(null);
  }, [selectionKey]);
  return (
    <>
      <Row>
        <EnableLabel>
          <Checkbox
            aria-label={__('Inner glow enabled', 'web-stories')}
            checked={enabled === true}
            ref={(input) => {
              if (input) input.indeterminate = enabled === MULTIPLE_VALUE;
            }}
            onChange={(event) => {
              const enabled = event.target.checked;
              pushUpdate(
                (element) => {
                  const textInnerGlow = {
                    ...settingsFor(element),
                    enabled,
                  };
                  return {
                    textInnerGlow,
                    ...getTextEffectAlignmentUpdate({
                      ...element,
                      textInnerGlow,
                    }),
                  };
                },
                true,
                { skipPresubmit: true }
              );
            }}
          />
          {__('Inner Glow', 'web-stories')}
        </EnableLabel>
        <ButtonAnchor ref={anchor}>
          <Button
            type={BUTTON_TYPES.TERTIARY}
            size={BUTTON_SIZES.SMALL}
            aria-haspopup="dialog"
            aria-label={__('Edit inner glow…', 'web-stories')}
            aria-expanded={isOpen}
            aria-controls={isOpen ? DIALOG_ID : undefined}
            onClick={(event) => {
              event.preventDefault();
              setSession(isOpen ? null : { key: selectionKey });
            }}
          >
            {__('Edit…', 'web-stories')}
          </Button>
        </ButtonAnchor>
      </Row>
      <Row>
        <Summary>{label}</Summary>
      </Row>
      <Popup
        anchor={anchor}
        dock={inspector}
        isOpen={isOpen}
        placement={PLACEMENT.LEFT_START}
        spacing={POPUP_SPACING}
        renderContents={({ propagateDimensionChange }) => (
          <InnerGlowDialog
            anchor={anchor}
            onResize={propagateDimensionChange}
            elements={selectedElements}
            onClose={() => setSession(null)}
            onUpdate={(update) => {
              // Glow changes paint only: use the inspector's synchronous path so
              // closing the popup cannot leave a deferred update on another selection.
              pushUpdate(update, true, { skipPresubmit: true });
            }}
          />
        )}
      />
    </>
  );
}
TextInnerGlowControls.propTypes = {
  selectedElements: PropTypes.array.isRequired,
  pushUpdate: PropTypes.func.isRequired,
};
export default TextInnerGlowControls;
